import { XMLParser } from "fast-xml-parser";
import { z } from "zod";

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: true,
  allowBooleanAttributes: true,
  isArray: (tagName) => {
    return [
      "patient",
      "doctor",
      "insurance",
      "FIELDS",
      "row",
      "encounter",
      "AdditionalNote",
      "coded",
      "uncoded",
      "problem",
    ].includes(tagName);
  },
  removeNSPrefix: true,
});

export function parseSoapEnvelope<S extends z.ZodTypeAny>(xml: string, schema: S): z.infer<S> {
  const result = z
    .object({ Envelope: z.object({ Body: z.object({ return: z.unknown() }) }) })
    .transform((parsed) => parsed.Envelope.Body.return)
    .pipe(schema)
    .safeParse(parser.parse(xml));

  if (!result.success) {
    throw result.error;
  }

  return result.data;
}

export function asArray<T>(v: T | T[] | undefined | null): T[] {
  if (v === undefined || v === null) return [];

  return Array.isArray(v) ? v : [v];
}

export function xmlArray<S extends z.ZodTypeAny>(item: S) {
  return z.preprocess(asArray, z.array(item));
}

export function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export enum SoapXsdType {
  STRING = "string",
  INT = "int",
}

export function buildSoapEnvelope(
  wrapper: string,
  fields: Record<string, string>,
  cdataKeys: readonly string[] = [],
  fieldTypes: Readonly<Record<string, SoapXsdType | undefined>> = {}
): string {
  const elements = Object.entries(fields)
    .map(([k, v]) => {
      const fieldType = fieldTypes[k] ?? SoapXsdType.STRING;

      return cdataKeys.includes(k)
        ? `<${k} xsi:type="xsd:${fieldType}"><![CDATA[${v.replace(/]]>/g, "]]]]><![CDATA[>")}]]></${k}>`
        : `<${k} xsi:type="xsd:${fieldType}">${escapeXml(v)}</${k}>`;
    })
    .join("");

  return (
    '<S:Envelope xmlns:S="http://schemas.xmlsoap.org/soap/envelope/" ' +
    'xmlns:SOAP-ENC="http://schemas.xmlsoap.org/soap/encoding/" ' +
    'S:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/" ' +
    'xmlns:xsd="http://www.w3.org/1999/XMLSchema" ' +
    'xmlns:xsi="http://www.w3.org/1999/XMLSchema-instance">' +
    "<Body>" +
    '<m:NOOP xmlns:m="NOOP">' +
    `<${wrapper}>` +
    elements +
    `</${wrapper}>` +
    "</m:NOOP>" +
    "</Body>" +
    "</S:Envelope>"
  );
}
